/**
 * <BenchmarkCard> — F044 §9.1 / §7. Renders DARK ("Benchmarks activate when peer data is available")
 * until peer data is real. When live: states provenance + N ("based on 214 anonymized environments,
 * financial services, APAC") and per-card metric directionality. NEVER renders a synthetic "Global"
 * median as market data (the procurement-defensibility fix — F044 Executive Summary).
 */
import type { FC } from 'react';
import { Body1, Caption1, Card, CardHeader, Title3, makeStyles, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  card: {
    padding: tokens.spacingVerticalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  dark: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
  },
  provenance: {
    color: tokens.colorNeutralForeground3,
  },
  value: {
    fontSize: tokens.fontSizeHero700,
  },
});

export interface BenchmarkData {
  metricLabel: string;
  /** The tenant's own value for this metric. */
  tenantValue: number;
  /** The peer benchmark value — undefined/null means "not yet available", which forces the dark state. */
  peerValue?: number;
  /** How many anonymised peers contributed — REQUIRED whenever `peerValue` is present. */
  sampleSize?: number;
  /** e.g. "financial services, APAC" — REQUIRED whenever `peerValue` is present. */
  cohortDescription?: string;
  higherIsBetter: boolean;
}

export interface BenchmarkCardProps {
  data: BenchmarkData;
}

export const BenchmarkCard: FC<BenchmarkCardProps> = ({ data }) => {
  const styles = useStyles();
  const hasRealPeerData = data.peerValue !== undefined && data.peerValue !== null && !!data.sampleSize && !!data.cohortDescription;

  if (!hasRealPeerData) {
    return (
      <Card className={`${styles.card} ${styles.dark}`}>
        <CardHeader header={<Title3>{data.metricLabel}</Title3>} />
        <Body1>Benchmarks activate when peer data is available.</Body1>
      </Card>
    );
  }

  const better = data.higherIsBetter ? data.tenantValue >= data.peerValue! : data.tenantValue <= data.peerValue!;

  return (
    <Card className={styles.card}>
      <CardHeader header={<Title3>{data.metricLabel}</Title3>} />
      <div className={styles.value}>{data.tenantValue}</div>
      <Body1>
        Peer median {data.peerValue} — {better ? 'ahead of' : 'behind'} peers ({data.higherIsBetter ? 'higher is better' : 'lower is better'})
      </Body1>
      <Caption1 className={styles.provenance}>
        Based on {data.sampleSize} anonymized environments, {data.cohortDescription}
      </Caption1>
    </Card>
  );
};
